import { useRef, useEffect } from "react";
import { Check, Circle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { BOMItem } from "@/types/chemistry";

interface BOMItemListProps {
  items: BOMItem[];
  activeItemId: string | null;
  onSelectItem: (itemId: string) => void;
}

export function BOMItemList({ items, activeItemId, onSelectItem }: BOMItemListProps) {
  const activeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeItemId]);

  return (
    <ScrollArea className="h-full">
      <div className="py-2">
        {items.map((item) => {
          const isActive = item.id === activeItemId;
          const isSourced = !!item.supplierName;

          return (
            <div
              key={item.id}
              ref={isActive ? activeRef : undefined}
              className={`
                flex items-center gap-2.5 px-3 py-2 mx-1 rounded-md cursor-pointer transition-colors text-sm
                ${isActive
                  ? "bg-primary/10 border-l-2 border-l-primary"
                  : "hover:bg-accent/50 border-l-2 border-l-transparent"
                }
              `}
              onClick={() => onSelectItem(item.id)}
            >
              {isSourced ? (
                <div className="w-4 h-4 rounded-full bg-success flex items-center justify-center shrink-0">
                  <Check className="h-2.5 w-2.5 text-success-foreground" />
                </div>
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${isActive ? "font-medium" : ""}`}>
                  {item.chemicalName || item.materialName}
                </p>
                {item.chemicalName && (
                  <p className="text-[11px] text-muted-foreground font-mono truncate">{item.materialName}</p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  {item.quantity.toFixed(1)} {item.unit}
                  {item.supplierName && (
                    <Badge variant="secondary" className="ml-1.5 text-[9px] px-1 py-0">
                      {item.supplierName}
                    </Badge>
                  )}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
